import { defaultGameData, womenGameData } from "../data/game-data";
import { GAME_VERSION } from "../version";
import { GameState } from "../domain/entities";
import pako from 'pako';
import JSZip from 'jszip';

export class DataService {

    /**
     * Detects file type based on extension
     */
    private static getFileType(filename: string): 'json' | 'gzip' | 'zip' {
        const lower = filename.toLowerCase();
        if (lower.endsWith('.gz') || lower.endsWith('.json.gz')) {
            return 'gzip';
        }
        if (lower.endsWith('.zip')) {
            return 'zip';
        }
        return 'json';
    }

    /**
     * Decompresses gzip data and returns text
     */
    private static decompressGzip(data: Uint8Array): string {
        try {
            const decompressed = pako.ungzip(data, { to: 'string' });
            return decompressed;
        } catch (error) {
            throw new Error(`Failed to decompress gzip file: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    /**
     * Extracts JSON from zip file and returns text
     */
    private static async extractJsonFromZip(data: ArrayBuffer): Promise<string> {
        try {
            const zip = new JSZip();
            await zip.loadAsync(data);

            // Find the first .json file in the zip
            let jsonContent: string | null = null;
            for (const [filename, file] of Object.entries(zip.files)) {
                if (filename.toLowerCase().endsWith('.json') && !file.dir) {
                    jsonContent = await file.async('text');
                    break;
                }
            }

            if (!jsonContent) {
                throw new Error('No JSON file found in zip archive');
            }
            return jsonContent;
        } catch (error) {
            throw new Error(`Failed to extract JSON from zip file: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    /**
     * Reads file content based on file type (json, gzip, or zip)
     */
    private static readFileContent(file: File, fileType: 'json' | 'gzip' | 'zip'): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = async (event) => {
                try {
                    const result = event.target?.result;
                    let jsonText: string;

                    if (fileType === 'json') {
                        jsonText = result as string;
                    } else if (fileType === 'gzip') {
                        const uint8Array = new Uint8Array(result as ArrayBuffer);
                        jsonText = this.decompressGzip(uint8Array);
                    } else if (fileType === 'zip') {
                        jsonText = await this.extractJsonFromZip(result as ArrayBuffer);
                    } else {
                        throw new Error('Unknown file type');
                    }

                    resolve(jsonText);
                } catch (error) {
                    reject(error);
                }
            };

            reader.onerror = () => {
                reject(new Error("Failed to read the file."));
            };

            // Read as text for JSON, as binary for compressed files
            if (fileType === 'json') {
                reader.readAsText(file);
            } else {
                reader.readAsArrayBuffer(file);
            }
        });
    }

    static exportBaseData(gameType: 'mens' | 'womens') {
        try {
            const dataToExport = gameType === 'womens' ? womenGameData : defaultGameData;
            const jsonString = JSON.stringify(dataToExport, null, 2);
            const blob = new Blob([jsonString], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `pixel-soccer-manager-base-data-${gameType}-v${GAME_VERSION}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to export base data", error);
            alert("Error exporting data. See console for details.");
        }
    }

    static exportCurrentData(gameState: GameState) {
        try {
            const leaguesToExport = JSON.parse(JSON.stringify(gameState.leagues));

            Object.values(leaguesToExport).forEach((league: any) => {
                league.teams.forEach((team: any) => {
                    team.played = 0;
                    team.won = 0;
                    team.drawn = 0;
                    team.lost = 0;
                    team.goalsFor = 0;
                    team.goalsAgainst = 0;
                    team.points = 0;
                    
                    team.finances.history = [{
                        season: 1,
                        week: 0, 
                        event: 'Initial Balance', 
                        amount: team.finances.balance
                    }];

                    team.squad.forEach((player: any) => {
                        player.gamesPlayed = 0;
                        player.goalsScored = 0;
                        player.goalsConceded = 0;
                        player.injury = null;
                        player.lastSeasonChange = 0;
                        player.history = [];
                    });
                });
            });

            const dataToExport = {
                version: GAME_VERSION,
                gameType: gameState.gameType,
                leagues: leaguesToExport,
            };

            const jsonString = JSON.stringify(dataToExport, null, 2);
            const blob = new Blob([jsonString], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `pixel-soccer-manager-custom-data-S${gameState.season}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Failed to export current game data", error);
            alert("Error exporting data. See console for details.");
        }
    }

    static async importData(file: File): Promise<any> {
        try {
            const fileType = this.getFileType(file.name);
            const jsonText = await this.readFileContent(file, fileType);
            const data = JSON.parse(jsonText);

            if (this.validateData(data)) {
                return data;
            } else {
                throw new Error("Invalid data structure or version.");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw error;
            }
            throw new Error("Failed to parse file.");
        }
    }

    private static validateData(data: any): boolean {
        // Basic validation - check for version and leagues structure.
        // A more robust implementation would use a JSON schema validator.
        if (typeof data !== 'object' || data === null) return false;
        if (data.version !== GAME_VERSION) {
            alert(`Version mismatch! Expected v${GAME_VERSION}, but file is v${data.version}.`);
            return false;
        }
        if (typeof data.leagues !== 'object' || data.leagues === null) return false;
        
        const leagueNames = Object.keys(data.leagues);
        if (leagueNames.length === 0) return false;

        const firstLeague = data.leagues[leagueNames[0]];
        if (!Array.isArray(firstLeague.teams) || firstLeague.teams.length === 0) return false;

        const firstTeam = firstLeague.teams[0];
        return 'name' in firstTeam && 'squad' in firstTeam && 'finances' in firstTeam && 'colors' in firstTeam;
    }

    static async exportFullGameState(gameState: GameState) {
        try {
            if (!gameState || !gameState.gameStarted) {
                alert("No active game to export.");
                return;
            }

            const teamName = gameState.playerTeamName.replace(/\s+/g, '-');
            const season = gameState.season;
            const week = Math.floor(gameState.currentWeek);
            const jsonString = JSON.stringify(gameState, null, 2);
            
            // Check for CompressionStream support to zip the file
            if ('CompressionStream' in window) {
                const filename = `PixelSoccerManager_${teamName}_S${season}W${week}.json.gz`;
                const stream = new Blob([jsonString]).stream();
                const compressedStream = stream.pipeThrough(new CompressionStream('gzip'));
                const blob = await new Response(compressedStream).blob();

                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                // Fallback for older browsers - export uncompressed
                console.warn("CompressionStream API not supported. Exporting as uncompressed JSON.");
                const filename = `PixelSoccerManager_${teamName}_S${season}W${week}.json`;
                const blob = new Blob([jsonString], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }

        } catch (error) {
            console.error("Failed to export full game state", error);
            alert("Error exporting game state. See console for details.");
        }
    }

    static async importFullGameState(file: File): Promise<GameState> {
        try {
            const fileType = this.getFileType(file.name);
            const jsonText = await this.readFileContent(file, fileType);
            const data = JSON.parse(jsonText);

            // A simple validation to check if it looks like a game state file
            if (data.gameStarted === true && data.version && data.season >= 1 && data.leagues && data.playerTeamName) {
                return data as GameState;
            } else {
                throw new Error("Invalid game state file structure.");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw error;
            }
            throw new Error("Failed to parse file.");
        }
    }
}